/**
 * Toasts on top of antd's static `notification`, keeping the call signature the app already
 * uses. Static (not `App.useApp()`) on purpose: most callers are zustand reducers outside any
 * React tree. `src/lib/theme.tsx` wires up `holderRender` so these still pick up the theme.
 *
 * ponytail: deliberately only the subset the app calls — no `promise`, `onChange`, `TYPE`,
 * `isActive` or transitions. Add one when a call site actually needs it.
 */
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import { notification, Progress } from 'antd';
import type { ArgsProps } from 'antd/es/notification/interface';
import type { ReactNode } from 'react';

export type ToastId = string;
type ToastType = 'success' | 'info' | 'warning' | 'error';
type ToastContent = ReactNode | { title: ReactNode; message: ReactNode };

/**
 * Options with no antd counterpart. Accepted so the call sites did not all have to be
 * rewritten, ignored on purpose: antd cards are always closable and never draggable, and
 * their only progress bar is the one we render ourselves.
 */
interface IgnoredToastOptions {
  isLoading?: boolean;
  hideProgressBar?: boolean;
  closeButton?: boolean;
  closeOnClick?: boolean;
  draggable?: boolean;
}

interface ToastOptions extends IgnoredToastOptions {
  /** Milliseconds until the toast closes, or `false` to keep it open. */
  autoClose?: number | false;
  position?: 'top-center' | 'top-right';
  /** Fraction between 0 and 1, or `null` to remove the bar again. */
  progress?: number | null;
  /** Overrides the severity implied by the called method. */
  type?: ToastType;
}

interface ToastUpdateOptions extends ToastOptions {
  render?: ReactNode;
}

/** antd has no fractional progress, so render one into the notification body. */
const progressBar = (progress: number): ReactNode => (
  <Progress percent={Math.round(progress * 100)} size="small" showInfo={false} />
);

const placements: Record<NonNullable<ToastOptions['position']>, ArgsProps['placement']> = {
  'top-center': 'top',
  'top-right': 'topRight',
};

/**
 * antd's `open({ key })` replaces the whole card, while callers expect react-toastify's
 * merge-into-the-live-toast semantics. Remember the last args per key so `update` can merge.
 */
const liveArgs = new Map<ToastId, ArgsProps>();

/** Only emits the antd props the caller actually mentioned, so `update` can merge safely. */
const toOptionArgs = ({
  autoClose,
  position,
  progress,
  type,
}: ToastOptions): Partial<Pick<ArgsProps, 'placement' | 'duration' | 'description' | 'type'>> => ({
  ...(type === undefined ? {} : { type }),
  ...(position === undefined ? {} : { placement: placements[position] }),
  ...(autoClose === undefined
    ? {}
    : { duration: autoClose === false ? 0 : autoClose / OneSecondInMillis }),
  ...(progress === undefined
    ? {}
    : { description: progress == null ? null : progressBar(progress) }),
});

const open = (args: ArgsProps): void => {
  liveArgs.set(args.key as ToastId, args);
  notification.open(args);
};

/** A bare node becomes the headline; a pair fills antd's title + description. */
const toContentArgs = (content: ToastContent): Pick<ArgsProps, 'title' | 'description'> =>
  content != null && typeof content === 'object' && 'title' in content
    ? { title: content.title, description: content.message }
    : { title: content };

const show = (type: ToastType, content: ToastContent, options: ToastOptions = {}): ToastId => {
  const key = shortIdBuilder();
  open({
    key,
    type,
    placement: 'topRight',
    ...toContentArgs(content),
    ...toOptionArgs(options),
  });
  return key;
};

const dismiss = (key?: ToastId): void => {
  if (key == null) {
    liveArgs.clear();
  } else {
    liveArgs.delete(key);
  }
  notification.destroy(key);
};

const level =
  (type: ToastType) =>
  (content: ToastContent, options?: ToastOptions): ToastId =>
    show(type, content, options);

export const toast = Object.assign(level('info'), {
  success: level('success'),
  info: level('info'),
  warn: level('warning'),
  error: level('error'),
  /** antd has no spinner notification; an info card that stays open until updated is the equivalent. */
  loading: (content: ToastContent, options?: ToastOptions): ToastId =>
    show('info', content, { autoClose: false, ...options }),

  update: (key: ToastId, { render, ...options }: ToastUpdateOptions): void => {
    const previous = liveArgs.get(key);
    if (previous == null) return;

    open({
      ...previous,
      ...(render === undefined ? {} : { title: render }),
      ...toOptionArgs(options),
    });
  },

  dismiss,
  /** react-toastify let a finished loading toast close itself; antd just closes it. */
  done: dismiss,
});
